import { IonApp, setupIonicReact } from '@ionic/react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { OnlineProvider } from '@/hooks/useOnline';
import { ToastProvider } from '@/hooks/useToast';
import { CaptureProvider } from '@/capture/CaptureContext';
import { LoginScreen } from '@/screens/Login/LoginScreen';
import { ForgotPasswordScreen } from '@/screens/Login/ForgotPasswordScreen';
import { DashboardScreen } from '@/screens/Dashboard/DashboardScreen';
import { EstimatesScreen } from '@/screens/Estimates/EstimatesScreen';
import { QueueScreen } from '@/screens/Queue/QueueScreen';
import { SetupScreen } from '@/screens/Capture/SetupScreen';
import { EntryScreen } from '@/screens/Capture/EntryScreen';
import { ReviewScreen } from '@/screens/Capture/ReviewScreen';
import { SuccessScreen } from '@/screens/Capture/SuccessScreen';
import { DetailScreen } from '@/screens/Detail/DetailScreen';
import { DataHubScreen } from '@/screens/Data/DataHubScreen';
import { HarvestWeeksScreen } from '@/screens/Data/HarvestWeeksScreen';
import { ImportScreen } from '@/screens/Data/ImportScreen';
import { UserFarmsScreen } from '@/screens/Data/UserFarmsScreen';
import { AdminFilterProvider } from './AdminFilters';

// Ionic is used for components only. Routing is plain React Router: the design forbids page
// transition choreography, so IonRouterOutlet's animated stack is not needed.
setupIonicReact({ mode: 'md', rippleEffect: false, animated: false });

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { phase } = useAuth();
  const loc = useLocation();
  if (phase === 'booting') return <div className="fw-frame" />;
  if (phase === 'signedOut') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function PublicOnly({ children }: { children: React.ReactElement }) {
  const { phase } = useAuth();
  if (phase === 'booting') return <div className="fw-frame" />;
  if (phase === 'signedIn') return <Navigate to="/" replace />;
  return children;
}

/** Master data screens: role admin only. Anyone else lands on Home. */
function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

const guarded = (el: React.ReactElement) => <RequireAuth>{el}</RequireAuth>;
const adminOnly = (el: React.ReactElement) => (
  <RequireAuth>
    <RequireAdmin>{el}</RequireAdmin>
  </RequireAuth>
);

export function App() {
  return (
    <IonApp>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <OnlineProvider>
          <AuthProvider>
            <ToastProvider>
              <CaptureProvider>
                <AdminFilterProvider>
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        <PublicOnly>
                          <LoginScreen />
                        </PublicOnly>
                      }
                    />
                    <Route
                      path="/forgot-password"
                      element={
                        <PublicOnly>
                          <ForgotPasswordScreen />
                        </PublicOnly>
                      }
                    />
                    <Route path="/" element={guarded(<DashboardScreen />)} />
                    <Route path="/estimates" element={guarded(<EstimatesScreen />)} />
                    <Route path="/estimates/:id" element={guarded(<DetailScreen />)} />
                    <Route path="/capture" element={guarded(<SetupScreen />)} />
                    <Route path="/capture/entry" element={guarded(<EntryScreen />)} />
                    <Route path="/capture/review" element={guarded(<ReviewScreen />)} />
                    <Route path="/capture/success" element={guarded(<SuccessScreen />)} />
                    <Route path="/waiting" element={guarded(<QueueScreen />)} />
                    <Route path="/data" element={adminOnly(<DataHubScreen />)} />
                    <Route path="/data/harvest-weeks" element={adminOnly(<HarvestWeeksScreen />)} />
                    <Route path="/data/user-farms" element={adminOnly(<UserFarmsScreen />)} />
                    <Route
                      path="/data/harvest-weeks/import"
                      element={adminOnly(<ImportScreen />)}
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AdminFilterProvider>
              </CaptureProvider>
            </ToastProvider>
          </AuthProvider>
        </OnlineProvider>
      </BrowserRouter>
    </IonApp>
  );
}
